import { useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AlertCircle, Upload } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { PageLayout } from '@/layouts'

const schema = z.object({
  id: z.string().optional(),
  id_Tenant: z.string().optional(),
  typeBank: z.string().min(1, 'Selecione o tipo de banco'),
  client_id: z.string().min(1, 'ID do cliente é obrigatório'),
  cliente_secret: z.string().min(1, 'Segredo do cliente é obrigatório'),
  x_conta_corrente: z.string().min(1, 'Número da conta corrente é obrigatório'),
  chaveKey: z.instanceof(File).optional().nullable(),
  certificado: z.instanceof(File).optional().nullable(),
  nome: z.string().min(1, 'Nome é obrigatório'),
  cpfCnpj: z.string().min(1, 'CPF/CNPJ é obrigatório'),
  tipoPessoa: z.string().min(1, 'Selecione o tipo de pessoa'),
  cep: z.string().min(8, 'CEP deve ter pelo menos 8 caracteres'),
  endereco: z.string().min(1, 'Endereço é obrigatório'),
  bairro: z.string().min(1, 'Bairro é obrigatório'),
  cidade: z.string().min(1, 'Cidade é obrigatória'),
  uf: z.string().min(2, 'UF deve ter 2 caracteres').max(2, 'UF deve ter 2 caracteres')
})

type BankFormData = z.infer<typeof schema>

export default function BankRegistration() {
  const [isEditing, setIsEditing] = useState(false)
  const [keyFileName, setKeyFileName] = useState<string>('')
  const [certificateFileName, setCertificateFileName] = useState<string>('')
  const [alert, setAlert] = useState<{ show: boolean; message: string }>({
    show: false,
    message: ''
  })

  const keyInputRef = useRef<HTMLInputElement>(null)
  const certificateInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<BankFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: '',
      id_Tenant: '',
      typeBank: '',
      client_id: '',
      cliente_secret: '',
      x_conta_corrente: '',
      chaveKey: null,
      certificado: null,
      nome: '',
      cpfCnpj: '',
      tipoPessoa: '',
      cep: '',
      endereco: '',
      bairro: '',
      cidade: '',
      uf: ''
    }
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'chaveKey' | 'certificado') => {
    const file = e.target.files?.[0] || null
    form.setValue(fileType, file)
    if (fileType === 'chaveKey') {
      setKeyFileName(file ? file.name : '')
    } else {
      setCertificateFileName(file ? file.name : '')
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const onSubmit = async (data: BankFormData) => {
    try {
      // Simulate API call to save form data
      await new Promise(resolve => setTimeout(resolve, 1000))
      console.log('Saving bank info:', data)

      setAlert({ show: true, message: 'Form data saved successfully.' })
      setIsEditing(false)
    } catch (error) {
      setAlert({
        show: true,
        message: 'Error saving form data. Please try again.'
      })
    }

    // Clear the alert after 3 seconds
    setTimeout(() => setAlert({ show: false, message: '' }), 3000)
  }

  const handleSave = () => {
    form.handleSubmit(onSubmit)()
  }

  const handleFileSave = async () => {
    const chaveKey = form.getValues('chaveKey')
    const certificado = form.getValues('certificado')

    try {
      // Simulate API calls to save files
      if (chaveKey) {
        await new Promise(resolve => setTimeout(resolve, 500))
        console.log('Saving key file:', chaveKey)
      }

      if (certificado) {
        await new Promise(resolve => setTimeout(resolve, 500))
        console.log('Saving certificate file:', certificado)
      }

      setAlert({
        show: true,
        message: 'Key and Certificate files saved successfully.'
      })
    } catch (error) {
      setAlert({
        show: true,
        message: 'Error saving Key and Certificate files. Please try again.'
      })
    }

    // Clear the alert after 3 seconds
    setTimeout(() => setAlert({ show: false, message: '' }), 3000)
  }

  return (
    <PageLayout
      title="Registro Bancário"
      footer={
        <>
          {!isEditing && (
            <Button onClick={handleEdit} className="bg-primary text-primary-foreground hover:bg-primary-hover">
              Editar
            </Button>
          )}
          {isEditing && (
            <Button onClick={handleSave} className="bg-primary text-primary-foreground hover:bg-primary-hover">
              Salvar Formulário
            </Button>
          )}
        </>
      }
    >
      {alert.show && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Informação</AlertTitle>
          <AlertDescription>{alert.message}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form id="bank-form" className="space-y-6">
          {/* Dados de Autenticação */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Dados de Autenticação</h3>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="client_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ID do Cliente</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly={!isEditing} className="bg-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cliente_secret"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Segredo do Cliente</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" readOnly={!isEditing} className="bg-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="x_conta_corrente"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número da Conta Corrente</FormLabel>
                  <FormControl>
                    <Input {...field} readOnly={!isEditing} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Arquivos de Segurança */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Arquivos de Segurança</h3>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="chaveKey"
                render={() => (
                  <FormItem>
                    <FormLabel>Chave</FormLabel>
                    <FormControl>
                      <div className="flex items-center space-x-2">
                        <Input
                          type="file"
                          onChange={e => handleFileChange(e, 'chaveKey')}
                          className="hidden"
                          ref={keyInputRef}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => keyInputRef.current?.click()}
                          className="bg-primary text-primary-foreground hover:bg-primary-hover"
                        >
                          <Upload className="mr-2 h-4 w-4" /> Enviar Chave
                        </Button>
                        <span className="text-sm text-gray-500">{keyFileName || 'Nenhum arquivo selecionado'}</span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="certificado"
                render={() => (
                  <FormItem>
                    <FormLabel>Certificado</FormLabel>
                    <FormControl>
                      <div className="flex items-center space-x-2">
                        <Input
                          type="file"
                          onChange={e => handleFileChange(e, 'certificado')}
                          className="hidden bg-input"
                          ref={certificateInputRef}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => certificateInputRef.current?.click()}
                          className="bg-primary text-primary-foreground hover:bg-primary-hover"
                        >
                          <Upload className="mr-2 h-4 w-4" /> Enviar Certificado
                        </Button>
                        <span className="text-sm text-gray-500">
                          {certificateFileName || 'Nenhum arquivo selecionado'}
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button
              type="button"
              onClick={handleFileSave}
              disabled={!form.getValues('chaveKey') && !form.getValues('certificado')}
              className="bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              Salvar Chave e Certificado
            </Button>
          </div>

          {/* Dados Pessoais */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Dados Pessoais</h3>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="cpfCnpj"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPF/CNPJ</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly={!isEditing} className="bg-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="typeBank"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Banco</FormLabel>
                    <FormControl>
                      <Select disabled={!isEditing} onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo de banco" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INTER">Inter</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="nome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly={!isEditing} className="bg-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tipoPessoa"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Pessoa</FormLabel>
                    <FormControl>
                      <Select disabled={!isEditing} onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo de pessoa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fisica">Física</SelectItem>
                          <SelectItem value="juridica">Jurídica</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Endereço */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Endereço</h3>
            <FormField
              control={form.control}
              name="cep"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CEP</FormLabel>
                  <FormControl>
                    <Input {...field} readOnly={!isEditing} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="endereco"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Endereço</FormLabel>
                  <FormControl>
                    <Input {...field} readOnly={!isEditing} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="bairro"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bairro</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly={!isEditing} className="bg-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cidade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cidade</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly={!isEditing} className="bg-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="uf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estado</FormLabel>
                  <FormControl>
                    <Input {...field} readOnly={!isEditing} className="bg-input" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
    </PageLayout>
  )
}
